// Copyright ©️ 2026 eVolve MEP, LLC

import {
  type ShopConfiguration,
  type WorkCell,
  type TaskType,
  useEvolvePaginatedGet,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Button, getDefaultZIndex } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import type { WithNullable } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { TaskPickerModal } from 'modules/Shop/Tasks/TaskTypes/TaskPickerModal';

import { CreateEditWorkCellModal } from './CreateEditWorkCellModal';

type AssignTaskToWorkCellPopoverProps = {
  draftShopConfiguration: ShopConfiguration;
  taskTypes: TaskType[];
  onWorkCellCreated: (workCell: WorkCell) => void;
  onTaskTypesAssigned: (workCell: WorkCell) => void;
};

type ValidatedFormBody = {
  workCell: WorkCell;
};
type FormBody = WithNullable<ValidatedFormBody>;

const AssignTaskFields = ({
  draftShopConfiguration,
  taskTypes,
  onWorkCellCreated,
  onTaskTypesAssigned,
}: AssignTaskToWorkCellPopoverProps) => {
  const { apiCall: assignWorkCellTaskTypesApiCall } = useEvolvePost('shop/workCellTaskType');
  const paginatedGet = useEvolvePaginatedGet('shop/workCell', {
    defaultConfig: {
      params: {
        facilityId: draftShopConfiguration.facilityId,
        excludeUnassigned: true,
        shopConfigurationId: draftShopConfiguration.shopConfigurationId,
      },
    },
  });
  const form = useForm<FormBody>({
    initialValues: {
      workCell: null,
    },
    validate: {
      workCell: (v) => isNil(v),
    },
  });

  const onSubmit = async (rawValues: typeof form.values) => {
    const { workCell } = rawValues as ValidatedFormBody;
    await assignWorkCellTaskTypesApiCall({
      workCellId: workCell.workCellId,
      taskTypeIds: taskTypes.map((t) => t.taskTypeId),
    });
    onTaskTypesAssigned(workCell);
  };

  useFormPopover(form, onSubmit);

  return (
    <SearchableSelect
      label="Work Cell"
      paginatedGet={paginatedGet}
      searchKey="workCellName"
      required
      idKey="workCellId"
      getItemLabel={(d) => d.workCellName}
      clearable
      creatable
      createLabel="New work cell"
      creationModal={(defaultWorkCellName, { opened, onClose }) => (
        <CreateEditWorkCellModal
          opened={opened}
          onClose={onClose}
          shopConfiguration={draftShopConfiguration}
          defaultWorkCellName={defaultWorkCellName}
          keepMounted={false}
          // Ensures the modal appears above the popover, which by default has a higher z-index than modals
          zIndex={getDefaultZIndex('popover') + 1}
          onWorkCellSaved={(createdWorkCell) => {
            onWorkCellCreated(createdWorkCell);
            form.setFieldValue('workCell', createdWorkCell);
          }}
        />
      )}
      value={form.values.workCell}
      onChange={(v) => form.setFieldValue('workCell', v)}
    />
  );
};

const AssignTaskToWorkCellPopover = (props: AssignTaskToWorkCellPopoverProps) => {
  const { taskTypes } = props;
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <FormPopover
      opened={opened}
      onClose={close}
      confirmationText="Assign"
      target={
        <Button onClick={open} leftSection={<EvolveIcon icon="Pin" size="sm" />} disabled={taskTypes.length === 0}>
          Assign {taskTypes.length > 0 ? `${taskTypes.length} ` : ''}task{taskTypes.length === 1 ? '' : 's'} to work
          cell
        </Button>
      }
    >
      <AssignTaskFields {...props} />
    </FormPopover>
  );
};

type AssignTasksPickerModalProps = {
  draftShopConfiguration: ShopConfiguration;
  onWorkCellCreated: (workCell: WorkCell) => void;
  onTaskTypesAssigned: (workCell: WorkCell) => void;
};

export const AssignTasksPickerModal = ({
  draftShopConfiguration,
  onWorkCellCreated,
  onTaskTypesAssigned,
  ...props
}: ModalRegistryManagedProps & AssignTasksPickerModalProps) => (
  <TaskPickerModal
    title="Assign tasks"
    shopConfiguration={draftShopConfiguration}
    renderAction={(selectedTasks) => (
      <AssignTaskToWorkCellPopover
        draftShopConfiguration={draftShopConfiguration}
        taskTypes={selectedTasks}
        onWorkCellCreated={onWorkCellCreated}
        onTaskTypesAssigned={(workCell) => {
          onTaskTypesAssigned(workCell);
          props.onClose();
        }}
      />
    )}
    {...props}
  />
);
